/**
 * Copyright (c) 2021-2026 Kognitos, Inc. All rights reserved.
 */
import { StatsigProvider, useClientAsyncInit } from '@statsig/react-bindings';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';
import { useConfig } from '@/config/ConfigProvider';
import { FullPageLoader } from '@/shared/components';

interface IStatsigProviderWrapperProps {
  children: React.ReactNode;
}

/**
 * Maps short environment names to full Statsig tier names.
 *
 * @param {string | undefined} env - Short environment name (dev, stg, prod)
 * @returns {string} Full Statsig tier name
 */
function getStatsigTier(env: string | undefined): string {
  switch (env) {
    case 'prod':
      return 'production';
    case 'stg':
      return 'staging';
    default:
      return 'development';
  }
}

/**
 * Wraps the app with StatsigProvider for feature flags and analytics.
 *
 * Initializes Statsig SDK with client ID from runtime config.
 * User details are set from ProtectedRoute component once currentUser is available.
 *
 * @param {IStatsigProviderWrapperProps} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap
 * @returns {JSX.Element} StatsigProvider wrapper
 */
export function StatsigProviderWrapper({
  children,
}: IStatsigProviderWrapperProps) {
  const config = useConfig();

  // Initialize Statsig client with async initialization
  // User details will be set from ProtectedRoute component
  const { client } = useClientAsyncInit(
    config.statsigClientId || '',
    {},
    {
      environment: { tier: getStatsigTier(config.environment) },
      plugins: [new StatsigAutoCapturePlugin()],
    },
  );

  // If no client ID is configured, render children without Statsig
  if (!config.statsigClientId) {
    return <>{children}</>;
  }

  return (
    <StatsigProvider client={client} loadingComponent={<FullPageLoader />}>
      {children}
    </StatsigProvider>
  );
}
